import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';


import Container from '../Shared/Container';
import { STYLES, ROUTES } from '../../utils/consts';
import { ColourRampType, MapType, SelectedLayersType } from '../../types/dataTypes';
import useIsGovInParams from '../../hooks/useIsGovInParams';
import LegendItem from './LegendItem';
import ColourRamp from './ColourRamp';
import { calculateQuantiles, mapGetLayerExists } from '../../utils/mapDataHandler';
import { LAYER_IDS } from '../../utils/mapConsts';
import { GeoJSONSource } from 'maplibre-gl';
import { feature } from '@turf/turf';

type LegendProps = {
  map: MapType;
  selectedLayers: SelectedLayersType[];
};

function Legend({ map, selectedLayers }: LegendProps) {
  const mapCurrent = map.current;
  const isGovInParams = useIsGovInParams();
  const location = useLocation();

  const [visibleLayers, setVisibleLayers] = useState<SelectedLayersType[]>([]);
  const [removingLayers, setRemovingLayers] = useState<string[]>([]); // Track layers being removed

  const hasRasterLayer = visibleLayers.some((layer: SelectedLayersType) => layer.type === 'raster');
  const hasCircleLayer = visibleLayers.some((layer: SelectedLayersType) => layer.type === 'circle');

  useEffect(() => {
    const selectedLayerNames = selectedLayers.filter((layer) => layer.isSelected).map((layer) => layer.layerName);

    // Find layers to be removed
    const layersToRemove = visibleLayers.filter(layer => !selectedLayerNames.includes(layer.layerName));
    const layersToRemoveNames = layersToRemove.map(layer => layer.layerName);

    // Trigger fade-out animation by marking these layers as removing
    setRemovingLayers(layersToRemoveNames);

    // Remove layers after the animation ends (500ms duration)
    const timeout = setTimeout(() => {
      setVisibleLayers(selectedLayers.filter((layer) => layer.isSelected));
      setRemovingLayers([]); // Clear removing layers after removal
    }, 500);

    return () => clearTimeout(timeout);
  }, [selectedLayers]);
  
  if (location.pathname === `/${ROUTES.INFO}`) return null;

  return (
    <LegendWrapperStyle>
      <LegendContainerStyle align_items='end'>
        <Container margin={`0 0`} border={STYLES.BORDER_SECONDARY}>
          <LegendStyle>
              {visibleLayers.filter((layer: SelectedLayersType) => layer.type == 'marker').map((layer: SelectedLayersType) => (
                <LegendItem
                  key={layer.layerName}
                  isRemoving={removingLayers.includes(layer.layerName)}
                  layer={layer}
                />
              ))}
              {visibleLayers.filter((layer: SelectedLayersType) => layer.type == 'line').map((layer: SelectedLayersType) => (
                <LegendItem
                  key={layer.layerName}
                  isRemoving={removingLayers.includes(layer.layerName)}
                  layer={layer}
                />
              ))}
              {visibleLayers.filter((layer: SelectedLayersType) => layer.type == 'circle').map((layer: SelectedLayersType) => (
                <LegendItem
                  key={layer.layerName}
                  isRemoving={removingLayers.includes(layer.layerName)}
                  layer={layer}
                />
              ))}
          </LegendStyle>
        </Container>
      </LegendContainerStyle>
      <LegendContainerStyle align_items='end'>
        {hasRasterLayer ?
        <Container margin={`${STYLES.PAGE_MARGIN}px 0`} border={STYLES.BORDER_SECONDARY}>
            {visibleLayers.filter((layer: SelectedLayersType) => layer.type === 'raster').map((layer: SelectedLayersType) => (
              <ColourRamp key={layer.layerName} attributeName={layer.layerName} colourRamp={layer.colourRamp}/>
            ))}
        </Container>
      : null} 
      </LegendContainerStyle>     
    </LegendWrapperStyle>
  );
}

export default Legend;

const LegendWrapperStyle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const LegendContainerStyle = styled.div<{ align_items: string }>`
  display: flex;
  align-items: ${({ align_items }) => align_items};
`;

const LegendStyle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex-direction: column;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;